import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';

import type { BadgeVariant } from '@mercadoni/stark-ui';
import { SkBadgeComponent, SkButtonComponent, SkIconComponent, SkToastService } from '@mercadoni/stark-ui';

import type {
  LimitedText,
  ServerResponseData,
  ServerResponseMode,
  ServerResponseTab,
  ServerResponseTabId,
  ServerResponseView,
} from '../../../core/interfaces';
import { ThemeService } from '../../../core/services';
import { LocaleService } from '../../../core/services/i18n/locale.service';
import { translateNow, translationVersion } from '../../../core/services/i18n/translate-now';
import { NfDatePipe } from '../../pipes/nf-date.pipe';
import { copyJsonToClipboard } from '../../utils/json-view.util';
import { buildResponseTabs, classifyServerResponse, formatResponseText, formatSize, limitForDisplay } from '../../utils/server-response.util';
import { NfDialogComponent } from '../nf-dialog/nf-dialog.component';
import { NfMonacoEditorComponent } from '../nf-monaco-editor/nf-monaco-editor.component';

/**
 * The one modal that shows a server response: Fury's envelope or error read for the user
 * ("Resumen"), and the raw JSON, request and headers in a read-only code editor that copes
 * with very large bodies (display limit with a truncation notice, full text on "Copiar").
 *
 * Used by Process Records ("Ver completo", `mode="process-record"`: Response and Request tabs)
 * and by notifications and hard failures (`mode="notification"`). It shows what it receives as
 * text only; redaction belongs to the code that captured the response.
 */
@Component({
  selector: 'nf-server-response-modal',
  imports: [NfDatePipe, NfDialogComponent, NfMonacoEditorComponent, SkBadgeComponent, SkButtonComponent, SkIconComponent],
  templateUrl: './nf-server-response-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NfServerResponseModalComponent {
  protected readonly dateLocale = inject(LocaleService).dateLocale;
  private readonly toast = inject(SkToastService);
  private readonly theme = inject(ThemeService);

  /** Whether the modal is shown. */
  readonly isOpen = input<boolean>(false);
  /** Dialog title. */
  readonly title = input<string | undefined>(undefined);
  /** Entry point, which decides the tabs. */
  readonly mode = input<ServerResponseMode>('notification');
  /** The response to show. */
  readonly response = input<ServerResponseData>({});

  /** Whether to offer "Preguntar a LeapBot"; the host decides, since only some users can use the chat. */
  readonly canAskLeapBot = input<boolean>(false);

  /** The user asked to close the modal. */
  readonly closed = output<void>();

  /** The user asked LeapBot about this response; the host opens the chat with it. */
  readonly askLeapBot = output<void>();

  private readonly selectedId = signal<ServerResponseTabId | null>(null);

  protected readonly view = computed<ServerResponseView>(() => classifyServerResponse(this.response().body));
  protected readonly tabs = computed<ServerResponseTab[]>(() => {
    translationVersion(); // the Resumen tab label is translated
    return buildResponseTabs(this.response(), this.mode());
  });

  protected readonly text = computed(() => {
    translationVersion(); // rebuilt when the language changes
    return {
      title: this.title() ?? translateNow('common.serverResponse.title'),
      contentLabel: translateNow('common.serverResponse.contentLabel'),
      errorId: translateNow('common.serverResponse.errorId'),
      detail: translateNow('common.serverResponse.detail'),
      askLeapBot: translateNow('common.serverResponse.askLeapBot'),
      copy: translateNow('common.actions.copy'),
      close: translateNow('common.actions.close'),
    };
  });

  /** The selected tab, or the first one when none was chosen (or the chosen one is gone). */
  protected readonly activeTab = computed<ServerResponseTab | null>(() => {
    const tabs = this.tabs();
    return tabs.find((t) => t.id === this.selectedId()) ?? tabs[0] ?? null;
  });

  protected readonly editorTheme = computed(() => (this.theme.isDarkMode() ? 'vs-dark' : 'vs'));

  protected readonly statusVariant = computed<BadgeVariant>(() => {
    const status = this.response().status ?? 0;
    if (status === 0 || status >= 500) return 'danger';
    if (status >= 400) return 'warning';
    if (status >= 200 && status < 300) return 'success';
    return 'primary';
  });

  protected readonly path = computed(() => {
    const url = this.response().url;
    if (!url) return '';
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  });

  /** Full, untruncated text of the selected tab, before the display limit is applied. */
  private readonly fullText = computed<string>(() => {
    const data = this.response();
    switch (this.activeTab()?.id) {
      case 'summary': {
        const view = this.view();
        if (view.kind === 'message') return view.message ?? '';
        const shown = view.kind === 'envelope' ? view.payload : view.details;
        return shown === undefined ? '' : formatResponseText(shown);
      }
      case 'request':
        return classifyServerResponse(data.requestBody).kind === 'empty' ? '' : formatResponseText(data.requestBody);
      case 'headers':
        return formatResponseText(data.headers);
      case 'json':
      case 'response':
        return this.view().kind === 'empty' ? '' : formatResponseText(data.body);
      default:
        return '';
    }
  });

  /** Message of an envelope or error, cut like any other text. */
  protected readonly messageShown = computed<string>(() => limitForDisplay(this.view().message ?? '').text);

  protected readonly limited = computed<LimitedText>(() => limitForDisplay(this.fullText()));

  protected readonly truncation = computed<string | null>(() => {
    const { truncated, totalChars } = this.limited();
    translationVersion();
    return truncated ? translateNow('common.serverResponse.truncated', { size: formatSize(totalChars) }) : null;
  });

  /** True when the selected tab has nothing to show beyond its empty-state copy. */
  protected readonly tabIsEmpty = computed<boolean>(() => {
    const id = this.activeTab()?.id;
    if (id === 'request') return this.fullText() === '';
    if (id === 'response') return this.view().kind === 'empty';
    return false;
  });

  /** Copy shown instead of content: no tabs, or an empty Response/Request tab. */
  protected readonly emptyMessage = computed<string>(() => {
    translationVersion(); // rebuilt when the language changes
    const data = this.response();
    if (this.activeTab()?.id === 'request') return translateNow('common.serverResponse.emptyRequest');
    if (this.activeTab()?.id === 'response') return translateNow('common.serverResponse.emptyResponse');
    if (data.status === 0) return translateNow('errors.network');
    if (data.status === undefined) return data.message ?? '';
    return translateNow('common.serverResponse.emptyResponse');
  });

  protected select(id: ServerResponseTabId): void {
    this.selectedId.set(id);
  }

  /** Arrow keys, Home and End move between tabs, as the tab pattern expects. */
  protected onTabKeydown(event: KeyboardEvent, current: ServerResponseTabId): void {
    const tabs = this.tabs();
    const index = tabs.findIndex((t) => t.id === current);
    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    this.selectedId.set(tabs[next].id);
    queueMicrotask(() => document.getElementById(`nf-response-tab-${tabs[next].id}`)?.focus());
  }

  /** Copies the complete text of the selected tab, ignoring the display limit. */
  protected copy(): void {
    const body = this.response().body;
    const text = this.activeTab()?.id === 'summary' ? formatResponseText(body) : this.fullText();
    if (text === '') return;
    copyJsonToClipboard(text, this.toast);
  }
}
